import { useHeaderScrollSurface } from './ContinuousHero';
import { pageFrameStyle } from './PageFrame';
import { DetailHeading } from '../../features/browse/DetailShell';
import { BrowseHeading, BrowseNavigation } from '../../features/browse/BrowseShell';
import React, { useState } from 'react';
import { useSearchEditing } from '../../features/search/SearchEditingContext';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import '../../features/search/SearchHomeChrome.css';
import SearchEntry from '../../features/search/SearchEntry';
import SearchCategoryHeading from '../../pages/search/components/SearchCategoryHeading';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Search, NotificationsNone, AdminPanelSettingsOutlined } from '@mui/icons-material';
import QuickDestinations from '../responsive/QuickDestinations';
import PostpopWordmark from './PostpopWordmark';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useUserStore } from '../../stores/userStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { trackSearch, trackMenuClick } from '../../lib/gaEvents';
import { getPageTypeFromPath } from '../../lib/gaParams';
import { REFERRER_TYPE } from '../../constants/gaEnums';
import '../../pages/home/HomeDesign.css';
import './HeaderSearch.css';
import './AdminHeader.css';

export default function HomeHeader({
  explore = false,
  browse = null,
  detail = null,
  search = null,
}) {
  const headerRef = useHeaderScrollSurface();
  const location = useLocation();
  const editing = useSearchEditing();
  const searchHome = search === 'home';
  const pageType = getPageTypeFromPath(location.pathname);
  const { locale, t } = useWebLocale();
  const en = locale === 'en';
  const user = useUserStore((s) => s.user);
  const isAdmin = Boolean(user?.id && user.role === 'ADMIN');
  const unread = useNotificationStore((s) => s.unreadCount);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const showExploreNavigation = Boolean(explore || browse);
  const visit = (to) => {
    trackMenuClick({ targetPageType: getPageTypeFromPath(to), pageType }, user);
  };
  const searchLabel = en ? 'Search albums, artists, playlists' : '앨범, 아티스트, 플리 검색';
  const submit = (event) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) return;
    trackSearch({ searchTerm: value, pageType }, user);
    navigate(`/search/summary?q=${encodeURIComponent(value)}`);
  };
  return (
    <header
      ref={headerRef}
      className={`pp-home-header${isAdmin ? ' pp-admin-header' : ''}${search ? ' pp-search-header' : ''}${searchHome ? ' pp-search-home-header' : ''}${searchHome && editing?.active ? ' pp-search-home-active' : ''}${explore ? ' pp-explore-header' : ''}${browse || detail ? ' pp-browse-header' : ''}${showExploreNavigation ? ' pp-has-explore-subnav' : ''}`}
    >
      <div className="pp-home-header-inner pp-page-frame" style={pageFrameStyle}>
        {search && search !== 'summary' && !searchHome && (
          <SearchCategoryHeading
            category={search}
            query={new URLSearchParams(location.search).get('q') || ''}
            mobile
          />
        )}
        {searchHome && (
          <h1
            className="pp-search-home-title"
            style={appTextStyle(baseline.search.chrome.searchTitle)}
          >
            {t('search')}
          </h1>
        )}
        {browse && <BrowseHeading active={browse} mobile />}
        {detail && <DetailHeading kind={detail} mobile />}
        {explore && (
          <div className="pp-explore-mobile-heading" lang={locale}>
            <h1 style={appTextStyle(baseline.search.chrome.searchTitle)}>{t('explore')}</h1>
            <QuickDestinations />
          </div>
        )}
        <Link className="pp-home-brand" to="/" aria-label={`postpop · ${t('home')}`}>
          <PostpopWordmark />
        </Link>
        <nav className="pp-home-desktop-nav" aria-label={t('navigation')}>
          {[
            ['/', 'home'],
            ['/explore', 'explore'],
            ['/search', 'search'],
            ['/mypage', 'my'],
          ].map(([to, key]) => {
            const link = (
              <NavLink
                key={key}
                className={({ isActive }) =>
                  isActive || ((browse || detail) && key === 'explore') ? 'active' : undefined
                }
                to={to}
                end={to === '/'}
                state={{ referrerType: REFERRER_TYPE.MENU }}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  visit(to);
                }}
              >
                {t(key)}
              </NavLink>
            );
            return key === 'explore' ? (
              <div className="pp-explore-menu" key={key}>
                {link}
                {showExploreNavigation && <BrowseNavigation />}
              </div>
            ) : (
              link
            );
          })}
        </nav>
        <div className="pp-home-header-tools">
          {search ? (
            <div
              className={`pp-search-header-entry${search !== 'summary' && !searchHome ? ' pp-search-detail-entry' : ''}`}
            >
              <SearchEntry
                assist={search === 'summary' || searchHome}
                home={searchHome}
                compact
                headerLabel={searchLabel}
                initialValue={new URLSearchParams(location.search).get('q') || ''}
              />
            </div>
          ) : (
            <form role="search" className="pp-home-header-search" onSubmit={submit}>
              <button type="submit" aria-label={t('search')}>
                <Search aria-hidden="true" />
              </button>
              <input
                type="search"
                aria-label={searchLabel}
                placeholder={searchLabel}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
              />
            </form>
          )}
          <div className="pp-home-header-actions">
            {isAdmin && (
              <Link
                className="pp-home-icon pp-home-admin"
                to="/admin"
                aria-label={en ? 'Admin page' : '관리자 페이지'}
                title={en ? 'Admin page' : '관리자 페이지'}
                onClick={() => visit('/admin')}
              >
                <AdminPanelSettingsOutlined aria-hidden="true" />
              </Link>
            )}
            <Link
              className="pp-home-icon pp-home-notifications"
              to={user?.id ? '/notifications' : '/login?redirectUrl=%2Fnotifications'}
              aria-label={
                en
                  ? `Notifications${user?.id && unread > 0 ? ' · unread' : ''}`
                  : `알림${user?.id && unread > 0 ? ' · 읽지 않은 알림 있음' : ''}`
              }
            >
              <NotificationsNone aria-hidden="true" />
              {user?.id && unread > 0 ? <span className="pp-home-unread" /> : null}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
